import React from 'react';
import { Box, Typography, Link } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { AdvisorInfo } from '../../types/advisor';
import healthspanWealthLogo from '../../assets/healthspan-wealth-logo.png';

// The advisor/booking block lives in ThankYouStep; the §1 "Book the conversation
// now" link scrolls to this id.
export const BOOK_ANCHOR_ID = 'hsw-book-conversation';

const GREEN = '#2BA784';
const RED = '#d1453b';
const AMBER = '#b06a2c';
const GRAY = '#8a94a6';
const INACTIVE = '#e2e5ea';

// API qualifier (stable) -> approved client label + color + meter position.
const QUALIFIER: Record<string, { label: string; color: string; seg: 'left' | 'mid' | 'right' | null }> = {
  'Worth exploring': { label: 'Needs attention', color: RED, seg: 'left' },
  'Room to discuss': { label: 'Worth watching', color: AMBER, seg: 'mid' },
  'Solid': { label: 'On track', color: GREEN, seg: 'right' },
  'More information needed': { label: 'More information needed', color: GRAY, seg: null },
};
const qual = (q?: string) => QUALIFIER[q || ''] || { label: q || 'More information needed', color: GRAY, seg: null };

// One-line framing under each theme title (client-side copy, keyed by theme key).
const THEME_SUBTITLE: Record<string, string> = {
  health_habits: 'Everyday routines, and whether they hold up across a long retirement.',
  health_conditions: 'What you are managing now, and what it implies for cost and timing.',
  social_connection: 'Community ties and the people nearby when you need them.',
  care_support: 'Who you would rely on if something changed — and whether they are named.',
  caregiving_responsibilities: 'Who depends on you, and what that support may end up requiring.',
};
const THEME_TITLE: Record<string, string> = {
  health_habits: 'Health habits',
  health_conditions: 'Health conditions',
  social_connection: 'Social connection',
  care_support: 'Care & support',
  caregiving_responsibilities: 'Caregiving responsibilities',
};

interface SnapshotTheme { key: string; title: string; qualifier: string; body: string; conversation: string; }
interface PersonSnapshot { subject: 'primary' | 'partner'; themes: SnapshotTheme[]; }
interface PriorityQuestion { rank: number; subject: 'primary' | 'partner'; themeKey: string; question: string; qualifier: string; }
interface ClientResource { kind: string; title: string; url: string | null; matchReason?: string; }
interface ClientSnapshot {
  headline: string;
  introduction: string;
  themes: SnapshotTheme[];
  recommendedTopics?: string[];
  nextAction?: { label: string; kind: string };
  disclaimer?: string;
}
interface Bundle {
  clientSnapshot: ClientSnapshot;
  peopleClientSnapshots?: PersonSnapshot[];
  priorityQuestions?: PriorityQuestion[];
  clientResource?: ClientResource | null;
}

interface Props {
  result: Bundle | null;
  advisor?: AdvisorInfo | null;
  personal?: { hasPartner?: boolean; client1?: { firstName?: string }; client2?: { firstName?: string } };
}

const Meter: React.FC<{ seg: 'left' | 'mid' | 'right' | null; color: string }> = ({ seg, color }) => {
  const segs: Array<'left' | 'mid' | 'right'> = ['left', 'mid', 'right'];
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
      {segs.map((s) => (
        <Box key={s} sx={{ width: 22, height: 6, borderRadius: 3, bgcolor: seg === s ? color : INACTIVE }} />
      ))}
    </Box>
  );
};

const SectionHeading: React.FC<{ children: React.ReactNode; extra?: React.ReactNode }> = ({ children, extra }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
    <Typography variant="h6" fontWeight="bold">{children}</Typography>
    {extra}
  </Box>
);

const HealthSpanResult: React.FC<Props> = ({ result, advisor, personal }) => {
  if (!result || !result.clientSnapshot) return null;
  const { clientSnapshot, peopleClientSnapshots, priorityQuestions, clientResource } = result;

  const hasPartner = !!personal?.hasPartner;
  const firstNameFor = (subject: 'primary' | 'partner') =>
    (subject === 'partner' ? personal?.client2?.firstName : personal?.client1?.firstName) ||
    (subject === 'partner' ? 'Your partner' : 'You');

  const names = (() => {
    const a = personal?.client1?.firstName;
    const b = personal?.client2?.firstName;
    if (hasPartner && a && b) return `${a} and ${b}`;
    return a || 'there';
  })();

  // People to show per theme: real per-person snapshots, else a single primary row.
  const people: PersonSnapshot[] =
    peopleClientSnapshots && peopleClientSnapshots.length
      ? peopleClientSnapshots
      : [{ subject: 'primary', themes: clientSnapshot.themes }];

  const themeTitle = (key: string) =>
    clientSnapshot.themes.find((t) => t.key === key)?.title || THEME_TITLE[key] || key;
  const themeBodyFor = (person: PersonSnapshot, key: string) => person.themes.find((t) => t.key === key);

  const scrollToBook = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById(BOOK_ANCHOR_ID)?.scrollIntoView({ behavior: 'smooth' });
  };

  const guideLabel = clientResource?.title || 'one-page guide to health and money';

  return (
    <Box sx={{ maxWidth: 780, mx: 'auto' }}>
      {/* Thanks header */}
      <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.7rem', sm: '2.1rem' }, lineHeight: 1.2, mb: 2 }}>
        Thanks, {names} — your Wealth Report is on its way.
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Your advisor is working through the numbers and will follow up. In the meantime, we have put together a{' '}
        <b>Healthspan Wealth snapshot</b> from what you have told us so far.
      </Typography>
      <Box sx={{ textAlign: 'center', py: 2, borderTop: '1px solid rgba(0,0,0,0.08)', borderBottom: '1px solid rgba(0,0,0,0.08)', mb: 4 }}>
        <Link href={`#${BOOK_ANCHOR_ID}`} onClick={scrollToBook} sx={{ color: GREEN, fontWeight: 700, textDecoration: 'none', cursor: 'pointer' }}>
          Book the conversation now ↓
        </Link>
      </Box>

      {/* What to talk about first */}
      <SectionHeading
        extra={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
            <Typography variant="body2" component="span">from</Typography>
            <Box component="img" src={healthspanWealthLogo} alt="Healthspan Wealth" sx={{ height: 22, width: 'auto' }} />
            <Typography variant="body2" component="span" fontWeight={600} sx={{ color: 'text.primary' }}>Healthspan Wealth</Typography>
          </Box>
        }
      >
        What to talk about first
      </SectionHeading>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        PlanScout and Healthspan Wealth partner to give you a fuller view of the plan — the numbers, plus the health,
        care and connection that decide whether they hold.
      </Typography>

      {clientSnapshot.themes.map((theme) => (
        <Box key={theme.key} sx={{ py: 2.5, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <Typography variant="subtitle1" fontWeight="bold">{theme.title}</Typography>
          {THEME_SUBTITLE[theme.key] && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{THEME_SUBTITLE[theme.key]}</Typography>
          )}
          {people.map((person) => {
            const pt = themeBodyFor(person, theme.key);
            if (!pt) return null;
            const q = qual(pt.qualifier);
            return (
              <Box key={person.subject} sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, py: 0.75 }}>
                {hasPartner && (
                  <Typography sx={{ minWidth: 64, fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary', textTransform: 'uppercase' }}>
                    {firstNameFor(person.subject)}
                  </Typography>
                )}
                <Meter seg={q.seg} color={q.color} />
                <Typography sx={{ minWidth: 128, fontWeight: 600, color: q.color, fontSize: '0.9rem' }}>{q.label}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>{pt.body}</Typography>
              </Box>
            );
          })}
        </Box>
      ))}

      {/* Three questions to take with you */}
      {priorityQuestions && priorityQuestions.length > 0 && (
        <Box sx={{ mt: 5 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
            {priorityQuestions.length === 3 ? 'Three questions to take with you' : 'Questions to take with you'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You do not have to have answers — these are openings your advisor can build on. Take them with you, along with our
          </Typography>
          {/* Discussion guide pill — a real download link once HealthSpan supplies
              clientResource.url (same URL on the sync response, callback, and status). */}
          {clientResource?.url ? (
            <Link
              href={clientResource.url}
              target="_blank"
              rel="noopener"
              download
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 1, mb: 3, borderRadius: 5, bgcolor: 'rgba(43,167,132,0.1)', color: GREEN, fontWeight: 600, fontSize: '0.85rem', textDecoration: 'none', '&:hover': { bgcolor: 'rgba(43,167,132,0.18)' } }}
            >
              <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
              one-page guide to health and money
            </Link>
          ) : (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 1, mb: 3, borderRadius: 5, bgcolor: 'rgba(43,167,132,0.1)', color: GREEN, fontWeight: 600, fontSize: '0.85rem' }}>
              <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
              <span>{guideLabel} · PDF link to follow</span>
            </Box>
          )}

          {[...priorityQuestions].sort((a, b) => a.rank - b.rank).map((pq, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1.5, py: 1.5, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: RED, mt: 0.75, flexShrink: 0 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">{pq.question}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {hasPartner ? `${firstNameFor(pq.subject)} · ` : ''}{themeTitle(pq.themeKey)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default HealthSpanResult;
